import { Component, computed, effect, EventEmitter, inject, input, Output, output, viewChild } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { FloorplanMonitoringItemDetailsModalComponent } from "@components/floorplan-monitoring-item-details-modal/floorplan-monitoring-item-details-modal.component";
import { StandardImageComponent } from "@components/standard-image/standard-image.component";
import { StepSelectionType } from "@domain/product/monitoring/family/family-step";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { CheckBoxControl } from "@odx/angular/cdk/checkbox-control";
import { ModalService, ModalSize } from "@odx/angular/components/modal";
import { createMonitoringGroupedSourceItem } from "@project/monitoring/grouped-parts/monitoring-grouped-parts-source-item.factory";
import { isGroupedOption } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringGroupedPartSourceItem } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.types";
import { OptionDisplay } from "@project/monitoring/option-display.service";

@Component({
  selector: "app-monitoring-option-card-with-quantity",
  imports: [UiKitModule, StandardImageComponent],
  templateUrl: "./monitoring-option-card-with-quantity.component.html",
  styleUrl: "./monitoring-option-card-with-quantity.component.scss",
})
export class MonitoringOptionCardWithQuantityComponent {
  private modalService = inject(ModalService);

  readonly optionData = input.required<OptionDisplay>();
  readonly quantity = input<number>(0);
  readonly minValue = input<number>(0);
  readonly maxValue = input<number>(Infinity);
  readonly selectionMode = input<StepSelectionType | string>(StepSelectionType.PICK0TON);
  readonly isSelectedInput = input<boolean>(false);
  readonly validationInvalid = input<boolean>(false);
  @Output() select$ = new EventEmitter<boolean>();
  readonly category = input<MonitoringCategory | undefined>(undefined);
  readonly categoryLabel = input<string | undefined>(undefined);

  readonly quantityChanged = output<number>();

  protected readonly isSelected = computed(() => {
    if (this.selectionMode() === StepSelectionType.PICK0TON || this.selectionMode() === StepSelectionType.PICK1TON) {
      return this.quantity() > 0;
    } else if (this.selectionMode() === StepSelectionType.PICK1 || this.selectionMode() === StepSelectionType.PICK0TO1) {
      return this.isSelectedInput();
    }
    return false;
  });
  protected readonly isBinary = computed(() => this.maxValue() <= 1);

  protected get showError(): boolean {
    return this.validationInvalid() && !this.isSelected();
  }

  protected readonly checkbox = viewChild<CheckBoxControl>("checkbox");
  protected readonly groupModalSuffix = $localize`:@@monitoring.groupedParts.modal.components:Components`;
  protected readonly isGroup = computed(() => {
    const option = this.optionData()?.option;
    return isGroupedOption(option);
  });

  constructor() {
    effect(() => {
      if (
        (this.selectionMode() === StepSelectionType.PICK1 || this.selectionMode() === StepSelectionType.PICK0TO1) &&
        this.isSelectedInput() &&
        this.quantity() === 0
      ) {
        this.increment();
      }
    });
  }

  protected onCardClicked(): void {
    const cb = this.checkbox();
    const willBeChecked = cb ? !cb.checked : !this.isSelected();
    this.emitToggle(willBeChecked);
  }

  protected showGroupDetails(): void {
    if (!this.isGroup()) return;

    const option = this.optionData();
    const resolvedCategory = this.category() ?? MonitoringCategory.PATIENT_MONITORS;
    const title = option?.displayName ? `${option.displayName} - ${this.groupModalSuffix}` : this.groupModalSuffix;
    const quantity = this.getDetailsQuantity();
    const items: MonitoringGroupedPartSourceItem[] = [
      createMonitoringGroupedSourceItem({ option: option.option, quantity }, this.categoryLabel() ?? ""),
    ];

    this.modalService.open(FloorplanMonitoringItemDetailsModalComponent, {
      id: "monitoring-item-details-modal",
      size: ModalSize.LARGE,
      data: { items, category: resolvedCategory, title, showCategory: false },
    });
  }

  private getDetailsQuantity(): number {
    if (this.quantity() > 0) return this.quantity();

    return Math.min(Math.max(this.minValue(), 1), this.maxValue());
  }

  protected onCheckedChanged(checked: boolean): void {
    this.emitToggle(checked);
  }

  /**
   * Emits a toggle (select/deselect) via select$.
   * The parent (option-selector) handles both the selection toggle and the initial quantity.
   */
  private emitToggle(checked: boolean): void {
    this.select$.emit(checked);
  }

  protected onQuantityChanged(quantity: number): void {
    if (quantity == null || Number.isNaN(quantity)) {
      return;
    }

    const min = this.minValue();
    const max = this.maxValue();
    const clamped = Math.max(min, Math.min(max, Math.floor(quantity)));

    if (clamped !== this.quantity()) {
      this.quantityChanged.emit(clamped);
    }
  }

  protected increment(): void {
    this.onQuantityChanged(this.quantity() + 1);
  }

  protected decrement(): void {
    this.onQuantityChanged(this.quantity() - 1);
  }

  protected readonly StepSelectionType = StepSelectionType;
}
